import { Component, OnInit, ElementRef, OnDestroy, ChangeDetectorRef, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import ResizeObserver from 'resize-observer-polyfill';
import { ZIndexMngr } from '../common/ZIndexMngr';
import * as utils from '../common/utils';

const MAX_WIDTH = 1500;
const MAX_HEIGHT = 800;

class VideoDimensions {
  width: number;
  height: number;
}

class VideoPlayerDialogData extends VideoDimensions {
  videoUrl: string;
}

// Returns the dimensions of a video asynchronously
async function getVideoDimensionsPromise(url: string) {
  let promise = await new Promise((resolve) => {
    // create video element
    let video = document.createElement('video');

    // place a listener on it
    video.addEventListener("loadedmetadata", function () {
      // retrieve dimensions
      let dimensions = new VideoDimensions;
      dimensions.height = this.videoHeight;
      dimensions.width = this.videoWidth;
      // send back result
      resolve(dimensions);
    }, {once: true});

    // start download meta data
    video.src = url;
  });
  return promise;
}

// Create dialog data based on given video URL
async function createDialogData(videoUrl: string) {
  let videoSize = await getVideoDimensionsPromise(videoUrl) as VideoDimensions;

  let width = videoSize.width;
  let height = videoSize.height;
  let ratio = width / height;

  if (width > MAX_WIDTH) {
    width = MAX_WIDTH;
    height = width / ratio;
  }
  if (height > MAX_HEIGHT) {
    height = MAX_HEIGHT;
    width = height * ratio;
  }

  let dialogData = new VideoPlayerDialogData;
  dialogData.videoUrl = videoUrl;
  dialogData.width = width;
  dialogData.height = height;

  return dialogData;
}

@Component({
  selector: 'video-player',
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss']
})
export class VideoPlayerComponent extends ZIndexMngr implements OnInit, OnDestroy {
  
  static readonly DIALOG_ID = 'videoPlayer';

  videoTitle: string;
  videoSrc: string;

  playerWidth: string;
  playerHeight: number;
  
  private playerInitialHeight: number;
  private playerHeightDelta: number = 0;

  constructor(public dialogRef: MatDialogRef<VideoPlayerComponent>,
              private cdRef: ChangeDetectorRef,
              elementRef: ElementRef,
              @Inject(MAT_DIALOG_DATA) data: VideoPlayerDialogData) {
    super(elementRef);
    this.videoSrc = data.videoUrl;
    this.playerHeight = this.playerInitialHeight = data.height;
    this.playerWidth = `${data.width}px`;

    let splitUrl = data.videoUrl.split('/');
    let fileName = splitUrl[splitUrl.length - 1];

    this.videoTitle = fileName;
  }

  static async createDialogConfig(videoUrl: string) {
    let dialogData = await createDialogData(videoUrl) as VideoPlayerDialogData;
  
    let dialogConfig = utils.createDialogConfig(true, false, VideoPlayerComponent.DIALOG_ID);
    dialogConfig.data = dialogData;
    dialogConfig.panelClass = 'video-player-dialog';
    return dialogConfig;
  }
  
  ngOnInit() {
    this.dialogRaise();

    const ro = new ResizeObserver((entries, observer) => {
      if (entries.length == 1) {
        if (this.playerHeightDelta == 0) {
          if (entries[0].contentRect.height > 0)
            this.playerHeightDelta = entries[0].contentRect.height - this.playerInitialHeight;
        }
        else {
          this.playerHeight = entries[0].contentRect.height - this.playerHeightDelta;
          this.playerWidth = "unset";
        }
      }
    });
    
    ro.observe(this.elementRef.nativeElement.parentElement);
  }

  ngOnDestroy(): void {
    this.dialogClosed();
  }

  ngAfterViewChecked() {
    this.cdRef.detectChanges();
  }
}
